import { useState, FormEvent } from 'react';
import { toast } from 'react-toastify';
import { getApiBase } from '../utils/api';
import './LoginPage.css';

interface LoginPageProps {
    onLogin: (user: { id: string; email: string; name: string; role: string }, token: string) => void;
}

function LoginPage({ onLogin }: LoginPageProps) {
    const [isSignup, setIsSignup] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const endpoint = isSignup ? '/api/auth/register' : '/api/auth/login';
            const apiBase = getApiBase() || 'https://api.trycommerium.com';
            const body = isSignup
                ? { email, password, name }
                : { email, password };

            const response = await fetch(`${apiBase}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error('Authentication failed');
            }

            const data = await response.json();
            onLogin(data.user, data.token);
            toast.success(isSignup ? 'Welcome to Commerium!' : 'Welcome back!');
        } catch (error) {
            toast.error(isSignup ? 'Signup failed. Please try again.' : 'Login failed. Check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container animate-fade-in">
                <div className="login-header">
                    <img src="/commerium-logo.png" alt="Commerium" className="logo-image" />
                    <h1 className="gradient-text">Commerium</h1>
                    <p>AI-powered e-commerce for artisans</p>
                </div>

                <div className="login-card card-glass">
                    <h2>{isSignup ? 'Create Account' : 'Welcome Back'}</h2>

                    <form onSubmit={handleSubmit}>
                        {isSignup && (
                            <div className="form-group">
                                <label>Your Name</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Maria Santos"
                                    required
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@workshop.com"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                minLength={6}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-large submit-btn"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="loading-spinner"></span>
                            ) : (
                                isSignup ? 'Create Account' : 'Sign In'
                            )}
                        </button>
                    </form>

                    <div className="login-footer">
                        <p>
                            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                            <button
                                type="button"
                                className="link-btn"
                                onClick={() => setIsSignup(!isSignup)}
                            >
                                {isSignup ? 'Sign In' : 'Create one'}
                            </button>
                        </p>
                    </div>
                </div>

                <div className="features-preview">
                    <div className="feature">
                        <span>📸</span>
                        <span>Photo-to-Listing</span>
                    </div>
                    <div className="feature">
                        <span>📄</span>
                        <span>Delivery Sync</span>
                    </div>
                    <div className="feature">
                        <span>🌍</span>
                        <span>Multi-Channel</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;
